/**
 * The dark palette, keyed by the same token names as
 * `theme/foundations/colors.js` (nested Chakra tokens flattened with `-`, the
 * way Chakra names its CSS variables: `gray.100` → `gray-100`).
 *
 * How it reaches the page: every Chakra style prop resolves to a CSS variable
 * (`bg="secondary-25"` → `var(--kula-colors-secondary-25)`; the theme's
 * `cssVarPrefix` is `kula`), and `@kula-ai/components` reads its Tailwind
 * colours through `--kula-theme-<token>` channel variables. Re-declaring both
 * sets under `html[data-kula-theme="dark"]` re-themes everything that goes
 * through a token, with no per-component work. Hex literals, and
 * `colors['…']` values from colors.js used in inline styles, bypass both and
 * stay light — use `colorVar()` for those.
 *
 * The neutral (`secondary`) ramp is inverted, light tints become dark tints and
 * the dark "text" shades become light. `primary-500` stays the brand colour.
 * `static-white`, `on-brand`, `black` and the status/priority colours are
 * deliberately absent: they read the same in both themes.
 */

import { theme as chakraTheme } from '@chakra-ui/react';

/** Page background, one step below the card surface. */
const PAGE = '#14131E';
/** Card / panel / input surface — what `white` becomes. */
const SURFACE = '#1A1926';

const SECONDARY: Record<string, string> = {
  25: PAGE,
  50: '#232232',
  100: '#2E2C3F',
  200: '#3A3850',
  300: '#4B4862',
  400: '#67637F',
  500: '#847F9F',
  600: '#9F9ABA',
  700: '#B9B5D0',
  800: '#CFCBE2',
  900: '#E6E3F3',
  950: '#F4F3FA',
};

const PRIMARY: Record<string, string> = {
  25: '#1C1A38',
  50: '#221F45',
  100: '#2B2757',
  200: '#3A3578',
  300: '#8580F0',
  400: '#9C98FF',
  500: '#7D78FF',
  // Readable as text on dark surfaces and tinted chips (4.8:1 on primary-50)
  // while still carrying white text as a hover fill.
  600: '#9A94FF',
  700: '#B3B7FF',
  800: '#BDC1FF',
  900: '#D6D9FF',
  950: '#ECEDFF',
};

const BORDER = SECONDARY[100];

const PASTEL: Record<string, [light: string, dark: string]> = {
  1: ['#552619', '#FFA28A'],
  2: ['#12301F', '#6FD99A'],
  3: ['#2A1A4A', '#B88CFF'],
  4: ['#12293A', '#7EC3EA'],
  5: [SECONDARY[50], SECONDARY[700]],
  6: ['#3A2A10', '#F2A27A'],
  7: ['#10233D', '#8AB8FF'],
  8: ['#3A1426', '#F58AB8'],
  9: ['#2F2A0E', '#E8D36A'],
  10: ['#2F2610', '#E8C25A'],
  11: ['#33133A', '#E58AE5'],
  12: ['#0F2A2A', '#6FD3D3'],
};

const ERROR: Record<string, string> = {
  25: '#4A1D27',
  100: '#E4626F',
  200: '#E0505E',
  300: '#F39AA3',
  500: '#E0505E',
  600: '#F39AA3',
  700: '#F39AA3',
};

const SUCCESS: Record<string, string> = {
  100: '#0F3D36',
  200: '#1FC2A6',
  300: '#5ED9C2',
};

const WARNING: Record<string, string> = {
  100: '#4A3412',
  200: '#EDA145',
  300: '#F2BE6E',
};

// Chakra's default `gray` scale (still used as `gray.N` in ~80 places).
const GRAY: Record<string, string> = {
  50: PAGE,
  100: SECONDARY[50],
  200: SECONDARY[100],
  300: SECONDARY[200],
  400: SECONDARY[400],
  500: SECONDARY[500],
  600: SECONDARY[700],
  700: SECONDARY[800],
  800: SECONDARY[900],
  900: SECONDARY[950],
};

const prefixed = (prefix: string, scale: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(scale).map(([step, hex]) => [`${prefix}-${step}`, hex])
  );

/** `#rrggbb` blended toward `into` by `weight` of `from` (0–1). */
export const mixHex = (from: string, into: string, weight: number): string => {
  const channel = (hex: string, i: number) =>
    parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  return `#${[0, 1, 2]
    .map((i) =>
      Math.round(channel(from, i) * weight + channel(into, i) * (1 - weight))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')
    .toUpperCase()}`;
};

// Chakra's built-in hues, still used through `colorScheme` (badges, tags,
// alerts) and as `red.100`-style tokens. Light tints become dark tints of the
// same hue and the dark text shades become the light ones; the 300–600 fill
// range stays, so solid buttons keep their white text.
const CHAKRA_HUES = [
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'cyan',
  'purple',
  'pink',
] as const;

const chakraHueColors = (): Record<string, string> =>
  Object.fromEntries(
    CHAKRA_HUES.flatMap((hue) => {
      const scale = chakraTheme.colors[hue] as Record<number, string>;
      return [
        [`${hue}-50`, mixHex(scale[500], PAGE, 0.14)],
        [`${hue}-100`, mixHex(scale[500], PAGE, 0.22)],
        [`${hue}-200`, mixHex(scale[500], PAGE, 0.32)],
        [`${hue}-700`, scale[300]],
        [`${hue}-800`, scale[200]],
        [`${hue}-900`, scale[100]],
      ];
    })
  );

/** Token name (as in colors.js, flattened) → dark hex. */
export const DARK_COLORS: Record<string, string> = {
  white: SURFACE,
  'base-white': SURFACE,
  'base-black': SECONDARY[950],

  ...prefixed('secondary', SECONDARY),
  ...prefixed('primary', PRIMARY),
  ...prefixed('error', ERROR),
  ...prefixed('success', SUCCESS),
  ...prefixed('warning', WARNING),
  ...prefixed('gray', GRAY),
  ...chakraHueColors(),

  ...Object.fromEntries(
    Object.entries(PASTEL).flatMap(([n, [light, dark]]) => [
      [`pastel-${n}-light`, light],
      [`pastel-${n}-dark`, dark],
    ])
  ),
  'pastel-3-lightest': '#221639',

  // Status pills: a dark tint of the hue with a light tint for the text,
  // like GitHub's labels on a dark page.
  'status-success-bg': '#0F2E22',
  'status-success-fg': '#6FD99A',
  'status-warning-bg': '#3A2A10',
  'status-warning-fg': '#F2BE6E',
  'status-error-bg': '#3A1A1E',
  'status-error-fg': '#F39AA3',
  'status-neutral-bg': SECONDARY[100],

  // Dark-only tints: not declared in light mode, so a literal written as
  // `var(--kula-colors-tint-error-bg, #FFD1D1)` keeps its exact light colour
  // and only takes these in dark mode. For status colours that are close to,
  // but not exactly, a status token.
  'tint-success-bg': '#0F2E22',
  'tint-success-fg': '#6FD99A',
  'tint-warning-bg': '#3A2A10',
  'tint-warning-fg': '#F2BE6E',
  'tint-error-bg': '#3A1A1E',
  'tint-error-fg': '#F39AA3',

  // Dark-only accents from the light ramp, picked by design for dark mode:
  // icons on the dark page take the light theme's primary-25, and links
  // (candidate names) its primary-400. Read as
  // `var(--kula-dark-accent-icon, <light literal>)`.
  'accent-icon': '#F5F6FF',
  'accent-link': '#797DFF',
  // The AI gradient of the "Write with AI" button (jd-ai-editor/aiTheme.ts),
  // for AI controls whose light gradient ends too dark for the dark page.
  'ai-gradient-start': '#8A90FF',
  'ai-gradient-end': '#B15FDA',
  // The switch's off track keeps its light-mode grey (secondary-300).
  'switch-track': '#BFC3DF',

  'border-color': BORDER,
  'border-color1': BORDER,
  'input-fill': SECONDARY[50],
  'menu-hover': SECONDARY[50],
  'modal-border': BORDER,

  // Legacy `atsColors`.
  // Text-first tokens that are also solid fills under white text in a few
  // places: light enough to read on dark (≥4.5:1), dark enough to carry white.
  'ats-primary': '#8A84FF',
  // Dark-only: the light brand colour (light primary-500, the top of the
  // primary button's gradient) for fills design keeps the same in both modes:
  // `ats-primary` buttons and checked checkboxes.
  'brand-fill': '#605AFB',
  'ats-negative': '#E0505E',
  'ats-secondary': SURFACE,
  'ats-tertiary': SECONDARY[50],
  'ats-bg-primary': PAGE,
  'ats-border-color': BORDER,
  'ats-text-primary': '#ECEAF6',
  'ats-text-secondary': '#C8C5DA',
  'ats-text-tertiary': '#A9A6BF',
  'ats-text-light': '#8F8BA6',
  'ats-text-dark': '#ECEAF6',
  'hover-light': SECONDARY[50],
  'border-light': SECONDARY[200],
  'border-color-header': BORDER,
  'modal-header-footer-border': BORDER,

  // Legacy `light.*` scale (light-mode-only names, still referenced).
  'light-50': SURFACE,
  'light-100': SECONDARY[900],
  'light-200': BORDER,
  'light-300': '#8F8BA6',
  'light-border': BORDER,
  'light-background': SURFACE,
  'light-hover': SECONDARY[900],
  'light-text-primary': SECONDARY[700],
  'light-text-secondary': '#8F8BA6',
};

/**
 * `@kula-ai/design-tokens` names its Tailwind variables slightly differently
 * (`--kula-theme-pastel-1light`, `--kula-theme-base-white`). Map each of ours
 * onto that name. `base-white` is left out on purpose: kula uses
 * `text-base-white` for text on solid fills (Badge, Alert), and its surfaces
 * use Tailwind's literal `bg-white` instead, handled in `darkModeStyles`.
 */
const KULA_THEME_GROUPS = [
  'primary',
  'secondary',
  'success',
  'error',
  'warning',
  'pastel',
] as const;

export const toKulaThemeVarName = (token: string): string | null => {
  if (token === 'base-black') return '--kula-theme-base-black';
  const group = KULA_THEME_GROUPS.find((g) => token.startsWith(`${g}-`));
  if (!group) return null;
  const shade = token.slice(group.length + 1);
  // pastel-1-light → pastel-1light
  const kulaShade =
    group === 'pastel' ? shade.replace(/-(light|dark|lightest)$/, '$1') : shade;
  return `--kula-theme-${group}-${kulaShade}`;
};

/** `#1A1926` → `26 25 38`, the channel form the kula variables take. */
export const hexToChannels = (hex: string): string => {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw.slice(0, 6);
  const value = Number.parseInt(full, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255].join(' ');
};

/** CSS custom properties for the dark theme: Chakra's and kula's. */
export const buildDarkCssVariables = (): Record<string, string> => {
  const vars: Record<string, string> = {};
  Object.entries(DARK_COLORS).forEach(([token, hex]) => {
    vars[`--kula-colors-${token}`] = hex;
    // Dark-only alias: undeclared in light mode, so
    // `var(--kula-dark-white, #FCFCFC)` keeps an off-palette light literal
    // exactly as written and only themes it in dark mode.
    vars[`--kula-dark-${token}`] = hex;
    const kulaVar = toKulaThemeVarName(token);
    if (kulaVar) vars[kulaVar] = hexToChannels(hex);
  });
  return vars;
};

export const DARK_SURFACE = SURFACE;
export const DARK_PAGE = PAGE;
export const DARK_BORDER = BORDER;
